// SolarInstallationTopHeader.js - Solar Installation themed top header bar component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a Solar Installation themed top header bar section
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the top header is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The top header elements
 */
export const createSolarInstallationTopHeaderElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const topHeaderSectionClass = 'solar-top-header-section';
  const topHeaderContainerClass = 'solar-top-header-container';
  const topHeaderLeftClass = 'solar-top-header-left';
  const topHeaderRightClass = 'solar-top-header-right';
  const topHeaderInfoClass = 'solar-top-header-info';
  const topHeaderInfoItemClass = 'solar-top-header-info-item';
  const topHeaderIconClass = 'solar-top-header-icon';
  const topHeaderTextClass = 'solar-top-header-text';
  const topHeaderSocialClass = 'solar-top-header-social';
  const topHeaderSocialLinkClass = 'solar-top-header-social-link';
  const topHeaderDividerClass = 'solar-top-header-divider';

  // Desktop Styles - Dark green/teal background to differentiate from navbar
  const topHeaderSectionStyles = {
    position: 'relative',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#065f46',
    background: `linear-gradient(90deg, #065f46 0%, #047857 50%, #065f46 100%)`,
    color: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderBottom: '1px solid rgba(74, 222, 128, 0.3)',
    fontSize: '14px',
    fontFamily: '"Inter", sans-serif'
  };

  const topHeaderContainerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto'
  };

  const topHeaderLeftStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  };

  const topHeaderRightStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  };

  const topHeaderInfoStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  };

  const topHeaderInfoItemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#e2e8f0',
    fontSize: '14px',
    fontFamily: '"Inter", sans-serif'
  };

  const topHeaderIconStyles = {
    fontSize: '16px',
    color: '#4ade80'
  };

  const topHeaderTextStyles = {
    fontSize: '14px',
    color: '#e2e8f0',
    fontFamily: '"Inter", sans-serif'
  };

  const topHeaderSocialStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const topHeaderSocialLinkStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: 'rgba(74, 222, 128, 0.15)',
    border: '1px solid rgba(74, 222, 128, 0.4)',
    color: '#4ade80',
    transition: 'all 0.3s ease',
    textDecoration: 'none'
  };

  const topHeaderDividerStyles = {
    width: '1px',
    height: '20px',
    backgroundColor: 'rgba(74, 222, 128, 0.4)',
    margin: '0 10px'
  };

  // Tablet Styles - Hide social icons and divider
  const tabletTopHeaderLeftStyles = {
    ...topHeaderLeftStyles,
    gap: '20px'
  };

  const tabletTopHeaderInfoStyles = {
    ...topHeaderInfoStyles,
    gap: '20px'
  };

  const tabletTopHeaderRightStyles = {
    display: 'none' // Hide entire right section on tablet
  };

  const tabletTopHeaderContainerStyles = {
    ...topHeaderContainerStyles,
    justifyContent: 'center' // Center the content when right section is hidden
  };

  // Mobile Styles - Hide social icons and divider, stack info vertically
  const mobileTopHeaderSectionStyles = {
    ...topHeaderSectionStyles,
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const mobileTopHeaderContainerStyles = {
    ...topHeaderContainerStyles,
    flexDirection: 'column',
    gap: '10px',
    justifyContent: 'center'
  };

  const mobileTopHeaderLeftStyles = {
    ...topHeaderLeftStyles,
    gap: '15px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%'
  };

  const mobileTopHeaderInfoStyles = {
    ...topHeaderInfoStyles,
    gap: '15px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  };

  const mobileTopHeaderInfoItemStyles = {
    ...topHeaderInfoItemStyles,
    fontSize: '13px'
  };

  const mobileTopHeaderTextStyles = {
    ...topHeaderTextStyles,
    fontSize: '13px'
  };

  const mobileTopHeaderRightStyles = {
    display: 'none' // Hide entire right section on mobile
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(topHeaderSectionClass, topHeaderSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderContainerClass, topHeaderContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderLeftClass, topHeaderLeftStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderRightClass, topHeaderRightStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderInfoClass, topHeaderInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderInfoItemClass, topHeaderInfoItemStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderIconClass, topHeaderIconStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderTextClass, topHeaderTextStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderSocialClass, topHeaderSocialStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderSocialLinkClass, topHeaderSocialLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(topHeaderDividerClass, topHeaderDividerStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(topHeaderLeftClass, tabletTopHeaderLeftStyles);
    newRegistry[DEVICES.TABLET].set(topHeaderInfoClass, tabletTopHeaderInfoStyles);
    newRegistry[DEVICES.TABLET].set(topHeaderRightClass, tabletTopHeaderRightStyles);
    newRegistry[DEVICES.TABLET].set(topHeaderContainerClass, tabletTopHeaderContainerStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(topHeaderSectionClass, mobileTopHeaderSectionStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderContainerClass, mobileTopHeaderContainerStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderLeftClass, mobileTopHeaderLeftStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderInfoClass, mobileTopHeaderInfoStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderInfoItemClass, mobileTopHeaderInfoItemStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderTextClass, mobileTopHeaderTextStyles);
    newRegistry[DEVICES.MOBILE].set(topHeaderRightClass, mobileTopHeaderRightStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    topHeaderSectionClass,
    topHeaderContainerClass,
    topHeaderLeftClass,
    topHeaderRightClass,
    topHeaderInfoClass,
    topHeaderInfoItemClass,
    topHeaderIconClass,
    topHeaderTextClass,
    topHeaderSocialClass,
    topHeaderSocialLinkClass,
    topHeaderDividerClass
  ]));

  // Generate IDs for all elements
  const topHeaderSectionId = Date.now();
  const containerId = topHeaderSectionId + 1;
  const leftId = topHeaderSectionId + 2;
  const rightId = topHeaderSectionId + 3;
  const infoId = topHeaderSectionId + 4;
  const addressItemId = topHeaderSectionId + 5;
  const addressIconId = topHeaderSectionId + 6;
  const addressTextId = topHeaderSectionId + 7;
  const address2ItemId = topHeaderSectionId + 8;
  const address2IconId = topHeaderSectionId + 9;
  const address2TextId = topHeaderSectionId + 10;
  const phoneItemId = topHeaderSectionId + 11;
  const phoneIconId = topHeaderSectionId + 12;
  const phoneTextId = topHeaderSectionId + 13;
  const emailItemId = topHeaderSectionId + 14;
  const emailIconId = topHeaderSectionId + 15;
  const emailTextId = topHeaderSectionId + 16;
  const dividerId = topHeaderSectionId + 17;
  const socialId = topHeaderSectionId + 18;

  const newElements = [
    // Main Top Header Section Container
    {
      id: topHeaderSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Solar Top Header Section',
      parentId: null,
      className: topHeaderSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-section',
        'data-component': 'section',
        'data-section-type': 'top-header',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Top Header Container',
      parentId: topHeaderSectionId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderContainerClass,
      styles: {},
      children: [leftId, rightId],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-container'
      }
    },
    
    // Left Section
    {
      id: leftId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Top Header Left',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderLeftClass,
      styles: {},
      children: [infoId],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-left'
      }
    },
    
    // Company Info Container
    {
      id: infoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Top Header Info',
      parentId: leftId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderInfoClass,
      styles: {},
      children: [addressItemId, address2ItemId, phoneItemId, emailItemId],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-info'
      }
    },
    
    // Address Item
    {
      id: addressItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Top Header Address',
      parentId: infoId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderInfoItemClass,
      styles: {},
      children: [addressIconId, addressTextId],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-info-item'
      }
    },
    
    // Address Icon
    {
      id: addressIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Address Icon',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('image'),
      className: topHeaderIconClass,
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:map-marker.svg?color=%234ade80',
      children: [],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-icon',
        'alt': 'Address'
      }
    },
    
    // Address Text
    {
      id: addressTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Address Text',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('span'),
      className: topHeaderTextClass,
      styles: {},
      children: [],
      text: 'P-724 Block-N North Nazimabad West Karachi',
      attributes: {
        'data-element': 'solar-top-header-text'
      }
    },
    
    // Second Address Item
    {
      id: address2ItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Top Header Address 2',
      parentId: infoId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderInfoItemClass,
      styles: {},
      children: [address2IconId, address2TextId],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-info-item'
      }
    },
    
    // Second Address Icon
    {
      id: address2IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Address 2 Icon',
      parentId: address2ItemId,
      hierarchyName: getHierarchyName('image'),
      className: topHeaderIconClass,
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:map-marker.svg?color=%234ade80',
      children: [],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-icon',
        'alt': 'Address 2'
      }
    },
    
    // Second Address Text
    {
      id: address2TextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Address 2 Text',
      parentId: address2ItemId,
      hierarchyName: getHierarchyName('span'),
      className: topHeaderTextClass,
      styles: {},
      children: [],
      text: 'Shop # 2 Ayaz Appartment Block# 2 Gulshan-e-Iqbal Karachi',
      attributes: {
        'data-element': 'solar-top-header-text'
      }
    },
    
    // Phone Item
    {
      id: phoneItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Top Header Phone',
      parentId: infoId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderInfoItemClass,
      styles: {},
      children: [phoneIconId, phoneTextId],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-info-item'
      }
    },
    
    // Phone Icon
    {
      id: phoneIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Phone Icon',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('image'),
      className: topHeaderIconClass,
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:phone.svg?color=%234ade80',
      children: [],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-icon',
        'alt': 'Phone'
      }
    },
    
    // Phone Text
    {
      id: phoneTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Phone Text',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('span'),
      className: topHeaderTextClass,
      styles: {},
      children: [],
      text: '0301-2475122',
      attributes: {
        'data-element': 'solar-top-header-text'
      }
    },
    
    // Right Section
    {
      id: rightId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Top Header Right',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderRightClass,
      styles: {},
      children: [dividerId],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-right'
      }
    },
    
    // Divider
    {
      id: dividerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Solar Top Header Divider',
      parentId: rightId,
      hierarchyName: getHierarchyName('div'),
      className: topHeaderDividerClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'solar-top-header-divider'
      }
    },
    
    // Social Links Container
    // {
    //   id: socialId,
    //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    //   type: 'div',
    //   name: 'Solar Top Header Social',
    //   parentId: rightId,
    //   hierarchyName: getHierarchyName('div'),
    //   className: topHeaderSocialClass,
    //   styles: {},
    //   children: [facebookId, twitterId, linkedinId, instagramId],
    //   text: '',
    //   attributes: {
    //     'data-element': 'solar-top-header-social'
    //   }
    // },
    
    // // Facebook Link
    // {
    //   id: facebookId,
    //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    //   type: 'link',
    //   name: 'Solar Top Header Facebook',
    //   parentId: socialId,
    //   hierarchyName: getHierarchyName('link'),
    //   className: topHeaderSocialLinkClass,
    //   styles: {},
    //   children: [facebookIconId],
    //   text: '',
    //   attributes: {
    //     'data-element': 'solar-top-header-social-link',
    //     'href': '#',
    //     'aria-label': 'Facebook'
    //   }
    // },
    
    // // Facebook Icon
    // {
    //   id: facebookIconId,
    //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    //   type: 'image',
    //   name: 'Facebook Icon',
    //   parentId: facebookId,
    //   hierarchyName: getHierarchyName('image'),
    //   styles: {
    //     width: '14px',
    //     height: '14px'
    //   },
    //   src: 'https://api.iconify.design/mdi:facebook.svg?color=%234ade80',
    //   children: [],
    //   text: '',
    //   attributes: {
    //     'data-element': 'social-icon-img',
    //     'alt': 'Facebook'
    //   }
    // },
    
    // // Twitter Link
    // {
    //   id: twitterId,
    //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    //   type: 'link',
    //   name: 'Solar Top Header Twitter',
    //   parentId: socialId,
    //   hierarchyName: getHierarchyName('link'),
    //   className: topHeaderSocialLinkClass,
    //   styles: {},
    //   children: [twitterIconId],
    //   text: '',
    //   attributes: {
    //     'data-element': 'solar-top-header-social-link',
    //     'href': '#',
    //     'aria-label': 'Twitter'
    //   }
    // },
    
    // // Twitter Icon
    // {
    //   id: twitterIconId,
    //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    //   type: 'image',
    //   name: 'Twitter Icon',
    //   parentId: twitterId,
    //   hierarchyName: getHierarchyName('image'),
    //   styles: {
    //     width: '14px',
    //     height: '14px'
    //   },
    //   src: 'https://api.iconify.design/mdi:twitter.svg?color=%234ade80',
    //   children: [],
    //   text: '',
    //   attributes: {
    //     'data-element': 'social-icon-img',
    //     'alt': 'Twitter'
    //   }
    // },
    
    // // LinkedIn Link
    // {
    //   id: linkedinId,
    //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    //   type: 'link',
    //   name: 'Solar Top Header LinkedIn',
    //   parentId: socialId,
    //   hierarchyName: getHierarchyName('link'),
    //   className: topHeaderSocialLinkClass,
    //   styles: {},
    //   children: [linkedinIconId],
    //   text: '',
    //   attributes: {
    //     'data-element': 'solar-top-header-social-link',
    //     'href': '#',
    //     'aria-label': 'LinkedIn'
    //   }
    // },
    
    // // LinkedIn Icon
    // {
    //   id: linkedinIconId,
    //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    //   type: 'image',
    //   name: 'LinkedIn Icon',
    //   parentId: linkedinId,
    //   hierarchyName: getHierarchyName('image'),
    //   styles: {
    //     width: '14px',
    //     height: '14px'
    //   },
    //   src: 'https://api.iconify.design/mdi:linkedin.svg?color=%234ade80',
    //   children: [],
    //   text: '',
    //   attributes: {
    //     'data-element': 'social-icon-img',
    //     'alt': 'LinkedIn'
    //   }
    // },
    
    // // Instagram Link
    // {
    //   id: instagramId,
    //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    //   type: 'link',
    //   name: 'Solar Top Header Instagram',
    //   parentId: socialId,
    //   hierarchyName: getHierarchyName('link'),
    //   className: topHeaderSocialLinkClass,
    //   styles: {},
    //   children: [instagramIconId],
    //   text: '',
    //   attributes: {
    //     'data-element': 'solar-top-header-social-link',
    //     'href': '#',
    //     'aria-label': 'Instagram'
    //   }
    // },
    
    // // Instagram Icon
    // {
    //   id: instagramIconId,
    //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    //   type: 'image',
    //   name: 'Instagram Icon',
    //   parentId: instagramId,
    //   hierarchyName: getHierarchyName('image'),
    //   styles: {
    //     width: '14px',
    //     height: '14px'
    //   },
    //   src: 'https://api.iconify.design/mdi:instagram.svg?color=%234ade80',
    //   children: [],
    //   text: '',
    //   attributes: {
    //     'data-element': 'social-icon-img',
    //     'alt': 'Instagram'
    //   }
    // } 
  ];
  
  // Return all elements
  return {
    solarTopHeaderSection: newElements[0],
    elements: newElements
  };
};

export default createSolarInstallationTopHeaderElements;